import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

// PR-AY2b (mechanical apply of PR-AY2a pattern): use Tables<"vouchers"> so
// the max_uses/uses_count nullability comes from the DB schema. Drops the
// `(data as Record<string, unknown>).x` casts that hid the real types.
type VoucherRow = Tables<"vouchers">;
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Crown, Ticket, CheckCircle2, Loader2, Sparkles, Tag, AlertCircle } from "lucide-react";
import { formatPrice } from "@/utils/formatPrice";
import { format } from "date-fns";
import { sv } from "date-fns/locale";

const intervalLabels: Record<string, string> = { month: "månad", quarter: "kvartal", year: "år" };

export default function MembershipSection() {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const qc = useQueryClient();
  const [voucherCode, setVoucherCode] = useState("");
  const [voucherApplied, setVoucherApplied] = useState<Record<string, unknown> | null>(null);

  const { data: plans = [], isLoading: plansLoading } = useQuery({
    queryKey: ["membership-plans", organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("membership_plans")
        .select("*")
        .eq("organization_id", organization!.id)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!organization?.id,
  });

  const { data: membership, isLoading: memLoading } = useQuery({
    queryKey: ["my-membership", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("memberships")
        .select("*, membership_plans(*)")
        .eq("user_id", user!.id)
        .in("status", ["active", "pending"])
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const { data: punchCards = [], isLoading: punchLoading } = useQuery({
    queryKey: ["my-punch-cards", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("punch_cards")
        .select("*")
        .eq("user_id", user!.id)
        .eq("status", "active")
        .order("purchased_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user?.id,
  });

  const subscribeMutation = useMutation({
    mutationFn: async (planId: string) => {
      // T1b/F1: apply creates a bare pending row — the period is stamped at
      // ADMIN APPROVE per the plan's billing interval (G-030). The old
      // hardcoded +30 days here pre-set a wrong period for quarter/year plans
      // (and 'pending' itself was rejected by the status trigger until the
      // 20260702120000 migration added it to the allowed set).
      const { error } = await supabase.from("memberships").insert({
        user_id: user!.id,
        plan_id: planId,
        organization_id: organization!.id,
        status: "pending",
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-membership"] });
      toast.success("Din ansökan har skickats!");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const buyPunchCardMutation = useMutation({
    mutationFn: async () => {
      // Sprint D PR-AC dual-write: even for 0-values, write _minor + currency
      // for consistent pattern (Q4). Defensive trigger trg_dual_write_punch_cards_minor
      // would backfill if missing, but app-level is the source of truth.
      const { error } = await supabase.from("punch_cards").insert({
        user_id: user!.id,
        organization_id: organization!.id,
        name: "10-klipp",
        total_punches: 10,
        used_punches: 0,
        price_paid: 0,
        price_paid_minor: 0,
        currency: "SEK",
        status: "active",
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-punch-cards"] });
      toast.success("Klippkort köpt!");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const validateVoucherMutation = useMutation({
    mutationFn: async (code: string) => {
      const { data, error } = await supabase
        .from("vouchers")
        .select("*")
        .eq("organization_id", organization!.id)
        .eq("code", code.toUpperCase())
        .eq("is_active", true)
        .maybeSingle<VoucherRow>();
      if (error) throw error;
      if (!data) throw new Error("Ogiltig rabattkod");
      if (data.max_uses && (data.uses_count ?? 0) >= data.max_uses) {
        throw new Error("Rabattkoden har redan använts max antal gånger");
      }
      return data;
    },
    onSuccess: (data) => {
      setVoucherApplied(data);
      toast.success("Rabattkod giltig!");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const isLoading = plansLoading || memLoading || punchLoading;
  if (isLoading) return <div className="space-y-3">{[1,2].map(i => <Skeleton key={i} className="h-32" />)}</div>;

  return (
    <div className="space-y-6">
      {/* Pending membership */}
      {membership && membership.status === "pending" && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              Ansökan inskickad
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Din ansökan om medlemskap behandlas. Du meddelas när medlemskapet är aktivt.
            </p>
            <Badge variant="secondary" className="mt-2">Inväntar godkännande</Badge>
          </CardContent>
        </Card>
      )}

      {/* Active membership */}
      {membership && membership.status === "active" && (
        <Card className="border-primary/30 bg-primary/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Crown className="h-5 w-5 text-primary" />
              Ditt medlemskap
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-medium">{membership.membership_plans?.name}</span>
              <Badge variant="default">Aktivt</Badge>
            </div>
            {membership.current_period_end && (
              <p className="text-sm text-muted-foreground">
                Förnyas: {format(new Date(membership.current_period_end), "d MMMM yyyy", { locale: sv })}
              </p>
            )}
            {(membership.membership_plans?.booking_discount_percent ?? 0) > 0 && (
              <p className="text-sm flex items-center gap-1">
                <Sparkles className="h-3.5 w-3.5 text-primary" />
                {membership.membership_plans!.booking_discount_percent}% rabatt på bokningar
              </p>
            )}
            {membership.membership_plans?.free_cancellation && (
              <p className="text-sm flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                Fri avbokning
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Available plans (if no membership) */}
      {!membership && plans.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <Crown className="h-5 w-5" /> Bli medlem
          </h3>

          {/* Voucher field */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Tag className="h-4 w-4 text-muted-foreground shrink-0" />
                <Input
                  placeholder="Har du en rabattkod? Ange här"
                  value={voucherCode}
                  onChange={(e) => setVoucherCode(e.target.value)}
                  className="font-mono uppercase h-9"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => validateVoucherMutation.mutate(voucherCode)}
                  disabled={!voucherCode || validateVoucherMutation.isPending}
                >
                  {validateVoucherMutation.isPending ? <Loader2 className="animate-spin h-4 w-4" /> : "Använd"}
                </Button>
              </div>
              {voucherApplied && (
                <p className="text-xs text-primary mt-2 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  Rabattkod "{(voucherApplied as Record<string, unknown>).code}" tillagd!
                </p>
              )}
            </CardContent>
          </Card>

          <div className="grid gap-3">
            {plans.map((plan) => (
              <Card key={plan.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">{plan.name}</h4>
                      {plan.description && (
                        <p className="text-xs text-muted-foreground">{plan.description}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <span className="text-xl font-bold">{formatPrice(plan.price)}</span>
                      <span className="text-xs text-muted-foreground">/{intervalLabels[plan.interval]}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    {(plan.booking_discount_percent ?? 0) > 0 && (
                      <Badge variant="secondary">{plan.booking_discount_percent}% rabatt</Badge>
                    )}
                    {(plan.priority_booking_hours ?? 0) > 0 && (
                      <Badge variant="secondary">{plan.priority_booking_hours}h förtur</Badge>
                    )}
                    {plan.free_cancellation && (
                      <Badge variant="secondary">Fri avbokning</Badge>
                    )}
                  </div>
                  <Button
                    className="w-full"
                    onClick={() => subscribeMutation.mutate(plan.id)}
                    disabled={subscribeMutation.isPending}
                  >
                    {subscribeMutation.isPending ? <Loader2 className="animate-spin h-4 w-4" /> : null}
                    Ansök om medlemskap
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Punch cards */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2">
            <Ticket className="h-5 w-5" /> Klippkort
          </h3>
          <Button
            size="sm"
            variant="outline"
            className="gap-1"
            onClick={() => buyPunchCardMutation.mutate()}
            disabled={buyPunchCardMutation.isPending}
          >
            {buyPunchCardMutation.isPending ? <Loader2 className="animate-spin h-3 w-3" /> : null}
            Köp nytt klippkort
          </Button>
        </div>
        {punchCards.length === 0 ? (
          <p className="text-sm text-muted-foreground">Inga aktiva klippkort</p>
        ) : (
          punchCards.map((pc) => (
            <Card key={pc.id}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{pc.name}</span>
                  <Badge variant={pc.used_punches >= pc.total_punches ? "secondary" : "default"}>
                    {pc.total_punches - pc.used_punches} klipp kvar
                  </Badge>
                </div>
                <Progress value={(pc.used_punches / pc.total_punches) * 100} className="h-2" />
                <p className="text-xs text-muted-foreground">
                  Använt {pc.used_punches} av {pc.total_punches}
                  {pc.expires_at && ` · Giltig t.o.m. ${format(new Date(pc.expires_at), "d MMM yyyy", { locale: sv })}`}
                </p>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
